import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { userService } from '../../services'
import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '../../auth/auth.context'
import { useLocale } from '../../hooks/use-locale'
import PageLayout from '../../components/layout/page-layout'
import Table from '../../components/ui/table'
import { buttonVariants } from '../../components/ui/button-variants'
import { PageHeader } from '../../components/layout/page-header'
import { PagePanel } from '../../components/layout/page-panel'
import { pageInput } from '../../components/layout/page-styles'
import { Container } from '../../components/ui/container'
import { UserPlus, Trash2, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { roleToText, employeeFunctionToText } from '../../helpers/enum-to-text'

export default function UsersListPage() {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const { user, company } = useAuth()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, debouncedSearch],
    queryFn: () =>
      userService.getUsers({
        page,
        limit: 10,
        search: debouncedSearch || undefined,
        companyId: user?.role === 'SUPERADMIN' ? undefined : user?.companyId,
      }),
    placeholderData: keepPreviousData,
  })

  const limits = (company as any)?.limits as { maxLicenses: number } | undefined
  const employeeCount = (data?.users || []).filter((u: any) => u.role === 'EMPLOYEE').length
  const employeeLimitReached = !!(
    limits &&
    user?.role === 'ADMIN' &&
    employeeCount >= limits.maxLicenses
  )

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowDeleteConfirm(null)
      setDeletingUserId(null)
    },
    onError: () => {
      setDeletingUserId(null)
    },
  })

  const handleDelete = (userId: string) => {
    setDeletingUserId(userId)
    deleteUserMutation.mutate(userId)
  }

  const totalUsers = data?.total ?? 0
  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  return (
    <PageLayout
      isLoading={isLoading}
      error={error ? t('errors.loadUsers') : undefined}
      loadingMessage={t('users.loading')}
    >
      <Container>
        <PageHeader
          title={t('users.title')}
          subtitle={t('users.totalCount', { count: totalUsers })}
          action={
            !employeeLimitReached ? (
              <Link to='/users/new'>
                <Button variant='primary' size='md'>
                  <UserPlus className='w-4 h-4 mr-2' />
                  {t('users.createEmployee')}
                </Button>
              </Link>
            ) : (
              <Button variant='primary' size='md' disabled>
                <UserPlus className='w-4 h-4 mr-2' />
                {t('common.limitReached')}
              </Button>
            )
          }
        />

        <PagePanel padding>
          <div className='relative max-w-sm'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400' />
            <input
              type='text'
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder={t('users.searchPlaceholder')}
              className={pageInput}
            />
          </div>
        </PagePanel>

        <Table
          dense
          data={data?.users || []}
          rowKey={u => String(u.id)}
          emptyMessage={t('users.empty')}
          columns={[
            {
              header: t('users.title'),
              cell: userItem => (
                <Link to={`/users/${userItem.id}`} className='flex items-center gap-3 group'>
                  <div className='w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0'>
                    {userItem.firstName?.charAt(0)}
                    {userItem.lastName?.charAt(0)}
                  </div>
                  <div className='min-w-0'>
                    <div className='font-medium text-neutral-900 group-hover:text-primary-600 transition-colors truncate'>
                      {userItem.firstName} {userItem.lastName}
                    </div>
                    <div className='text-xs text-neutral-400 truncate'>
                      {userItem.email || userItem.phoneNumber || t('common.dash')}
                    </div>
                  </div>
                </Link>
              ),
            },
            {
              header: t('common.role'),
              cell: u => (
                <Badge
                  title={roleToText(u.role)}
                  variant={
                    u.role === 'ADMIN' ? 'warning' : u.role === 'SUPERADMIN' ? 'error' : 'default'
                  }
                />
              ),
              className: 'w-32',
            },
            {
              header: t('common.function'),
              cell: u => (
                <span className='text-sm text-neutral-700'>{employeeFunctionToText(u.function)}</span>
              ),
              className: 'w-28',
            },
            {
              header: t('common.hireDate'),
              cell: u => (
                <span className='text-sm text-neutral-600'>
                  {u.startDate ? formatDate(u.startDate) : t('common.dash')}
                </span>
              ),
              className: 'w-32',
            },
            {
              header: t('common.status'),
              cell: u => (
                <Badge
                  title={u.isActive ? t('common.active') : t('common.inactive')}
                  variant={u.isActive ? 'success' : 'error'}
                />
              ),
              className: 'w-24',
            },
            ...(user?.role === 'SUPERADMIN'
              ? [
                  {
                    header: t('common.company'),
                    cell: (u: any) => (
                      <span className='text-sm text-neutral-600'>{u.companyId || t('common.dash')}</span>
                    ),
                    className: 'w-36',
                  },
                ]
              : []),
            {
              header: '',
              cell: u => (
                <div className='flex items-center justify-end gap-1'>
                  <Link
                    to={`/users/${u.id}`}
                    className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                  >
                    {t('common.open')}
                  </Link>
                  <button
                    onClick={() => {
                      const uid = u.id ? String(u.id) : null
                      if (!uid) return
                      setShowDeleteConfirm(uid)
                    }}
                    className='inline-flex items-center justify-center h-8 w-8 rounded-lg text-neutral-400 hover:text-error-600 hover:bg-error-50 transition-colors'
                    title={t('common.delete')}
                  >
                    <Trash2 className='w-4 h-4' />
                  </button>
                </div>
              ),
              className: 'w-32',
            },
          ]}
        />

        {totalPages > 1 && (
          <div className='flex items-center justify-between'>
            <p className='text-sm text-neutral-500'>
              {t('common.pageOf', { page, totalPages, total: totalUsers })}
            </p>
            <div className='flex items-center gap-1'>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className='inline-flex items-center justify-center h-8 w-8 rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                <ChevronLeft className='w-4 h-4' />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) pageNum = i + 1
                else if (page <= 3) pageNum = i + 1
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i
                else pageNum = page - 2 + i
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`inline-flex items-center justify-center h-8 min-w-[2rem] px-2 rounded-lg text-sm font-medium transition-colors ${
                      pageNum === page
                        ? 'bg-primary-500 text-white'
                        : 'text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages}
                className='inline-flex items-center justify-center h-8 w-8 rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                <ChevronRight className='w-4 h-4' />
              </button>
            </div>
          </div>
        )}
      </Container>

      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-neutral-900/50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg shadow-[var(--shadow-lg)] p-6 max-w-md w-full mx-4'>
            <h3 className='text-base font-semibold text-neutral-900 mb-2'>
              {t('common.deleteUserTitle')}
            </h3>
            <p className='text-sm text-neutral-600 mb-6'>{t('common.deleteUserMessage')}</p>
            <div className='flex justify-end gap-3'>
              <Button variant='secondary' size='md' onClick={() => setShowDeleteConfirm(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant='destructive'
                size='md'
                onClick={() => handleDelete(showDeleteConfirm)}
                loading={deletingUserId === showDeleteConfirm}
                disabled={deletingUserId === showDeleteConfirm}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
